/**
 * Self-contained GS1 element-string decoder.
 *
 * Decodes the raw character stream produced by a USB keyboard-wedge scanner
 * reading a GS1-128 barcode or GS1 Data Matrix. It has no dependencies or DOM
 * access so it can be reused by client-side validation and automated tests.
 */

export const GS = '\x1D';

const AIM_PREFIX_RE = /^\](?:C1|d[12]|e[0-3]|Q[13])/;

// These characters are not part of the GS1 encodable character set, but some
// scanner configurations emit them instead of the group separator.
const GS_ALTERNATES = new Set(['\x1c', '\x1e', '~', '^', '\t', '\r', '\n', ' ']);

const AI_TABLE = {
  '00': { fixedLength: 18, name: 'SSCC' },
  '01': { fixedLength: 14, name: 'GTIN' },
  '02': { fixedLength: 14, name: 'CONTENT GTIN' },
  '10': { fixedLength: null, maxLength: 20, name: 'BATCH/LOT' },
  '11': { fixedLength: 6, name: 'PROD DATE', isDate: true },
  '12': { fixedLength: 6, name: 'DUE DATE', isDate: true },
  '13': { fixedLength: 6, name: 'PACK DATE', isDate: true },
  '15': { fixedLength: 6, name: 'BEST BY', isDate: true },
  '16': { fixedLength: 6, name: 'SELL BY', isDate: true },
  '17': { fixedLength: 6, name: 'EXPIRY', isDate: true },
  '20': { fixedLength: 2, name: 'VARIANT' },
  '21': { fixedLength: null, maxLength: 20, name: 'SERIAL' },
  '22': { fixedLength: null, maxLength: 20, name: 'CPV' },
  '235': { fixedLength: null, maxLength: 28, name: 'TPX' },
  '240': { fixedLength: null, maxLength: 30, name: 'ADDITIONAL ID' },
  '241': { fixedLength: null, maxLength: 30, name: 'CUST. PART NO.' },
  '250': { fixedLength: null, maxLength: 30, name: 'SECONDARY SERIAL' },
  '251': { fixedLength: null, maxLength: 30, name: 'REF. TO SOURCE' },
  '253': { fixedLength: null, maxLength: 30, name: 'GDTI' },
  '254': { fixedLength: null, maxLength: 20, name: 'GLN EXTENSION' },
  '30': { fixedLength: null, maxLength: 8, name: 'VAR. COUNT' },
  '37': { fixedLength: null, maxLength: 8, name: 'COUNT' },
  '400': { fixedLength: null, maxLength: 30, name: 'ORDER NUMBER' },
  '401': { fixedLength: null, maxLength: 30, name: 'GINC' },
  '402': { fixedLength: null, maxLength: 17, name: 'GSIN' },
  '403': { fixedLength: null, maxLength: 30, name: 'ROUTE' },
  '410': { fixedLength: 13, name: 'SHIP TO GLN' },
  '411': { fixedLength: 13, name: 'BILL TO GLN' },
  '412': { fixedLength: 13, name: 'PURCHASED FROM GLN' },
  '413': { fixedLength: 13, name: 'SHIP FOR GLN' },
  '414': { fixedLength: 13, name: 'LOC GLN' },
  '415': { fixedLength: 13, name: 'PAY TO GLN' },
  '416': { fixedLength: 13, name: 'PROD/SERV LOC GLN' },
  '417': { fixedLength: 13, name: 'PARTY GLN' },
  '420': { fixedLength: null, maxLength: 20, name: 'SHIP TO POST' },
  '421': { fixedLength: null, maxLength: 12, name: 'SHIP TO POST + ISO' },
  '422': { fixedLength: 3, name: 'ORIGIN' },
  '7003': { fixedLength: 10, name: 'EXPIRY TIME' },
  '710': { fixedLength: null, maxLength: 20, name: 'NHRN PZN' },
  '711': { fixedLength: null, maxLength: 20, name: 'NHRN CIP' },
  '712': { fixedLength: null, maxLength: 20, name: 'NHRN CN' },
  '713': { fixedLength: null, maxLength: 20, name: 'NHRN DRN' },
  '714': { fixedLength: null, maxLength: 20, name: 'NHRN AIM' },
  '8003': { fixedLength: null, maxLength: 30, name: 'GRAI' },
  '8004': { fixedLength: null, maxLength: 30, name: 'GIAI' },
  '8005': { fixedLength: 6, name: 'PRICE PER UNIT' },
  '8008': { fixedLength: null, maxLength: 12, name: 'PROD DATE/TIME' },
  '8012': { fixedLength: null, maxLength: 20, name: 'VERSION' },
  '8017': { fixedLength: 18, name: 'GSRN PROVIDER' },
  '8018': { fixedLength: 18, name: 'GSRN RECIPIENT' },
  '8020': { fixedLength: null, maxLength: 25, name: 'PAYMENT SLIP REF' },
  '90': { fixedLength: null, maxLength: 30, name: 'INTERNAL 90' },
  '91': { fixedLength: null, maxLength: 90, name: 'INTERNAL 91' },
  '92': { fixedLength: null, maxLength: 90, name: 'INTERNAL 92' },
  '93': { fixedLength: null, maxLength: 90, name: 'INTERNAL 93' },
  '94': { fixedLength: null, maxLength: 90, name: 'INTERNAL 94' },
  '95': { fixedLength: null, maxLength: 90, name: 'INTERNAL 95' },
  '96': { fixedLength: null, maxLength: 90, name: 'INTERNAL 96' },
  '97': { fixedLength: null, maxLength: 90, name: 'INTERNAL 97' },
  '98': { fixedLength: null, maxLength: 90, name: 'INTERNAL 98' },
  '99': { fixedLength: null, maxLength: 90, name: 'INTERNAL 99' },
  '310': { fixedLength: 6, name: 'NET WEIGHT kg', fourDigit: true },
  '311': { fixedLength: 6, name: 'LENGTH m', fourDigit: true },
  '312': { fixedLength: 6, name: 'WIDTH m', fourDigit: true },
  '313': { fixedLength: 6, name: 'HEIGHT m', fourDigit: true },
  '314': { fixedLength: 6, name: 'AREA m²', fourDigit: true },
  '315': { fixedLength: 6, name: 'NET VOLUME l', fourDigit: true },
  '316': { fixedLength: 6, name: 'NET VOLUME m³', fourDigit: true },
  '320': { fixedLength: 6, name: 'NET WEIGHT lb', fourDigit: true },
  '330': { fixedLength: 6, name: 'GROSS WEIGHT kg', fourDigit: true },
  '340': { fixedLength: 6, name: 'GROSS WEIGHT lb', fourDigit: true },
  '390': { fixedLength: null, maxLength: 15, name: 'AMOUNT', fourDigit: true },
  '391': { fixedLength: null, maxLength: 18, name: 'AMOUNT + ISO', fourDigit: true },
  '392': { fixedLength: null, maxLength: 15, name: 'PRICE', fourDigit: true },
  '393': { fixedLength: null, maxLength: 18, name: 'PRICE + ISO', fourDigit: true },
};

export function isValidGtin(gtin) {
  if (!/^\d{8}$|^\d{12,14}$/.test(gtin)) return false;
  const digits = gtin.split('').map(Number);
  const check = digits.pop();
  let sum = 0;
  for (let i = digits.length - 1, weight = 3; i >= 0; i--, weight = 4 - weight) {
    sum += digits[i] * weight;
  }
  return (10 - (sum % 10)) % 10 === check;
}

export function parseGs1Date(yymmdd, now = new Date()) {
  if (!/^\d{6}$/.test(yymmdd)) return null;
  const yy = Number(yymmdd.slice(0, 2));
  const month = Number(yymmdd.slice(2, 4));
  let day = Number(yymmdd.slice(4, 6));
  if (month < 1 || month > 12) return null;

  const currentYear = now.getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  let year = currentCentury + yy;
  if (year - currentYear > 50) year -= 100;
  else if (year - currentYear < -49) year += 100;

  const daysInMonth = new Date(year, month, 0).getDate();
  if (day === 0) day = daysInMonth;
  if (day > daysInMonth) return null;

  const pad = (number) => String(number).padStart(2, '0');
  return {
    year,
    month,
    day,
    iso: `${year}-${pad(month)}-${pad(day)}`,
    display: `${pad(day)}/${pad(month)}/${year}`,
  };
}

function normalise(raw) {
  let value = String(raw);
  value = value.normalize('NFKC');
  value = value.replace(/[\u200B-\u200F\u2060\uFEFF\u00AD]/g, '');
  value = value.replace(/[\r\n\t]+$/g, '');
  value = value.replace(AIM_PREFIX_RE, '');
  value = value.replace(/\((\d{2,4})\)/g, `${GS}$1`);
  value = Array.from(value, (character) => (
    GS_ALTERNATES.has(character) ? GS : character
  )).join('');
  while (value.startsWith(GS)) value = value.slice(1);

  if (value.length >= 32 && value.length % 2 === 0) {
    const half = value.length / 2;
    if (value.slice(0, half) === value.slice(half)) value = value.slice(0, half);
  }
  return value;
}

function matchAi(value, index) {
  for (const length of [4, 3, 2]) {
    const candidate = value.slice(index, index + length);
    if (candidate.length < length || !/^\d+$/.test(candidate)) continue;
    if (AI_TABLE[candidate]) return { ai: candidate, spec: AI_TABLE[candidate] };
    if (length === 4) {
      const head = candidate.slice(0, 3);
      const spec = AI_TABLE[head];
      if (spec && spec.fourDigit) return { ai: candidate, spec };
    }
  }
  return null;
}

const NUMERIC_AI_RE = /^(00|01|02|20|30|31|32|33|34|35|36|37|39|41|422|7003|8005)/;

function structurallyValid(ai, spec, value) {
  if (spec.isDate) {
    if (!/^\d{6}$/.test(value)) return false;
    const month = Number(value.slice(2, 4));
    const day = Number(value.slice(4, 6));
    return month >= 1 && month <= 12 && day <= 31;
  }
  if (NUMERIC_AI_RE.test(ai) && !/^\d+$/.test(value)) return false;
  return true;
}

const MAX_PARSE_ATTEMPTS = 500;

function tryParse(value, index, state) {
  if (state.attempts++ > MAX_PARSE_ATTEMPTS) return null;
  while (index < value.length && value[index] === GS) index++;
  if (index >= value.length) return [];

  const match = matchAi(value, index);
  if (!match) return null;
  const { ai, spec } = match;
  const start = index + ai.length;

  if (spec.fixedLength !== null) {
    const elementValue = value.slice(start, start + spec.fixedLength);
    if (elementValue.length < spec.fixedLength || !structurallyValid(ai, spec, elementValue)) return null;
    const rest = tryParse(value, start + spec.fixedLength, state);
    return rest ? [{ ai, name: spec.name, value: elementValue }, ...rest] : null;
  }

  const groupSeparatorIndex = value.indexOf(GS, start);
  if (groupSeparatorIndex !== -1) {
    const elementValue = value.slice(start, groupSeparatorIndex);
    if (!elementValue || (spec.maxLength && elementValue.length > spec.maxLength)) return null;
    if (!structurallyValid(ai, spec, elementValue)) return null;
    const rest = tryParse(value, groupSeparatorIndex, state);
    return rest ? [{ ai, name: spec.name, value: elementValue }, ...rest] : null;
  }

  const remaining = value.length - start;
  if (remaining <= 0) return null;
  const max = spec.maxLength ?? remaining;

  if (remaining <= max) {
    const elementValue = value.slice(start);
    if (structurallyValid(ai, spec, elementValue)) {
      return [{ ai, name: spec.name, value: elementValue }];
    }
  }

  let bestParse = null;
  for (let length = Math.min(max, remaining - 2); length >= 1; length--) {
    const elementValue = value.slice(start, start + length);
    if (!structurallyValid(ai, spec, elementValue)) continue;
    const rest = tryParse(value, start + length, state);
    if (rest && rest.length) {
      const candidate = [{ ai, name: spec.name, value: elementValue }, ...rest];
      if (!bestParse || candidate.length > bestParse.length) bestParse = candidate;
    }
  }
  return bestParse;
}

function linearDiagnosticWalk(value, result) {
  let index = 0;
  while (index < value.length) {
    if (value[index] === GS) {
      index++;
      continue;
    }

    const match = matchAi(value, index);
    if (!match) {
      result.errors.push(`Unrecognised application identifier at "…${value.slice(index, index + 6)}".`);
      break;
    }

    const { ai, spec } = match;
    index += ai.length;
    let elementValue;

    if (spec.fixedLength !== null) {
      elementValue = value.slice(index, index + spec.fixedLength);
      if (elementValue.length < spec.fixedLength) {
        result.errors.push(`AI ${ai} (${spec.name}) is truncated — expected ${spec.fixedLength} characters, got ${elementValue.length}.`);
        break;
      }
      index += spec.fixedLength;
    } else {
      const groupSeparatorIndex = value.indexOf(GS, index);
      elementValue = groupSeparatorIndex === -1 ? value.slice(index) : value.slice(index, groupSeparatorIndex);
      index += elementValue.length;
      if (!elementValue) {
        result.errors.push(`AI ${ai} (${spec.name}) has no data.`);
        break;
      }
      if (spec.maxLength && elementValue.length > spec.maxLength) {
        result.errors.push(`AI ${ai} (${spec.name}) exceeds max length ${spec.maxLength} — the scanner may be stripping the group separator.`);
      }
    }

    result.elements.push({ ai, name: spec.name, value: elementValue });
    result.ais[ai] = elementValue;
  }
}

export function decodeGS1(raw) {
  const result = {
    ok: false,
    elements: [],
    ais: {},
    gtin: null,
    gtinValid: null,
    lot: null,
    serial: null,
    expiry: null,
    productionDate: null,
    errors: [],
    warnings: [],
  };

  const value = normalise(raw);
  if (!value) {
    result.errors.push('Empty scan.');
    return result;
  }

  let parsed = tryParse(value, 0, { attempts: 0 });

  if (!parsed || !parsed.length) {
    const anchor = value.search(/01\d{14}/);
    if (anchor > 0 && !/\d/.test(value[anchor - 1])) {
      parsed = tryParse(value.slice(anchor), 0, { attempts: 0 });
      if (parsed && parsed.length) {
        result.warnings.push(`Ignored ${anchor} unrecognised leading character(s): "${value.slice(0, anchor)}".`);
      }
    }
  }

  if (parsed && parsed.length) {
    result.elements = parsed;
    for (const element of parsed) result.ais[element.ai] = element.value;
  } else {
    linearDiagnosticWalk(value, result);
  }

  if (result.ais['01']) {
    result.gtin = result.ais['01'];
    result.gtinValid = isValidGtin(result.gtin);
    if (!result.gtinValid) result.errors.push(`GTIN ${result.gtin} has an invalid check digit.`);
  }
  if (result.ais['10']) result.lot = result.ais['10'];
  if (result.ais['21']) result.serial = result.ais['21'];
  if (result.ais['17']) {
    result.expiry = parseGs1Date(result.ais['17']);
    if (!result.expiry) result.errors.push(`AI 17 (EXPIRY) "${result.ais['17']}" is not a valid date.`);
  }
  if (result.ais['11']) result.productionDate = parseGs1Date(result.ais['11']);

  result.ok = result.errors.length === 0 && result.elements.length > 0;
  return result;
}
